import type { ReactNode } from 'react';
import type { PresenceStatus, PublicUser } from '@clanfire/shared';
import { MessageContent } from '@/components/chat/MessageContent';
import { Avatar, statusLabel } from '@/components/ui/Avatar';
import { Skeleton } from '@/components/ui/Spinner';
import { profileTheme } from '@/lib/profileStyle';
import { cn } from '@/lib/utils';
import { ProfileEffect } from './ProfileEffect';
import { ProfileBadges } from './ProfileBadges';
import { StaffBadge } from './StaffBadge';
import { StyledName } from './StyledName';

interface ProfileCardProps {
  user: PublicUser | null;
  presence?: PresenceStatus;
  /** Server nickname, shown instead of the display name. */
  nickname?: string | null;
  /** Icons next to the name (e.g. server owner crown). */
  badges?: ReactNode;
  /** Controls in the banner's top-right corner (e.g. a close button). */
  corner?: ReactNode;
  /** Extra sections below the bio (member since, roles, actions…). */
  children?: ReactNode;
  className?: string;
}

/**
 * A user's full profile: banner, decorated avatar, styled name,
 * status, formatted "About me", theme colours and animated effect.
 * Used by the profile popup and the live preview in profile settings.
 */
export function ProfileCard({ user, presence = 'offline', nickname, badges, corner, children, className }: ProfileCardProps) {
  const { style: themeStyle, themed } = profileTheme(user);
  const accent = user?.accentColor ?? '#c67139';
  const bannerTall = !!user?.bannerUrl;
  const bannerHeight = bannerTall ? 120 : 96;
  const edge = themed ? (user?.themePrimary ?? accent) : 'var(--clanfire-elevated)';

  return (
    <div className={cn('relative overflow-hidden', !themed && 'bg-elevated', className)} style={themeStyle}>
      <div className="relative" style={{ height: bannerHeight, background: user?.bannerUrl ? undefined : `linear-gradient(135deg, ${accent}, ${accent}55)` }}>
        {user?.bannerUrl && <img src={user.bannerUrl} alt="" className="h-full w-full object-cover" draggable={false} />}
        {corner && <div className="absolute top-3 right-3 z-20 flex gap-1.5">{corner}</div>}
      </div>
      <div className="absolute left-5 z-[1]" style={{ top: bannerHeight - 48 }}>
        <span className="block rounded-full border-[6px]" style={{ borderColor: edge, background: edge }}>
          <Avatar user={user} size={84} status={presence} ring={themed ? 'ring-black/25' : 'ring-elevated'} decorate />
        </span>
      </div>

      <div className="relative px-5 pt-14 pb-5">
        {!user ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-28" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <h2 className="min-w-0 truncate text-xl font-extrabold tracking-tight">
                <StyledName user={user}>{nickname || user.displayName}</StyledName>
              </h2>
              {user.staffRole && <StaffBadge role={user.staffRole} size="md" />}
              {badges}
            </div>
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-fg-soft">
              <span>
                {nickname && <span>{user.displayName} · </span>}@{user.username} · <span className="text-fg-muted">{statusLabel[presence]}</span>
              </span>
              <ProfileBadges user={user} />
            </p>
            {user.customStatus && (
              <p className="mt-2 text-sm text-fg-soft">
                {user.customStatusEmoji} {user.customStatus}
              </p>
            )}
          </>
        )}
        {children}
      </div>

      {user?.profileEffect && <ProfileEffect id={user.profileEffect} className="z-10" />}
    </div>
  );
}

/** "About me" rendered with the same (safe) markdown as messages. */
export function ProfileBio({ bio }: { bio: string }) {
  if (!bio.trim()) return null;
  return (
    <section>
      <h3 className="mb-1 text-[11px] font-bold tracking-wider text-fg-muted uppercase">About me</h3>
      <MessageContent content={bio} interactive className="text-fg-soft" />
    </section>
  );
}
